import { IconType } from '@react-icons/all-files';
import { FiCalendar } from '@react-icons/all-files/fi/FiCalendar';
import { FiCamera } from '@react-icons/all-files/fi/FiCamera';
import { FiCheck } from '@react-icons/all-files/fi/FiCheck';
import { FiCreditCard } from '@react-icons/all-files/fi/FiCreditCard';
import { FiEdit3 } from '@react-icons/all-files/fi/FiEdit3';
import { FiFileText } from '@react-icons/all-files/fi/FiFileText';
import { FiFlag } from '@react-icons/all-files/fi/FiFlag';
import { FiImage } from '@react-icons/all-files/fi/FiImage';
import { FiMapPin } from '@react-icons/all-files/fi/FiMapPin';
import { FiTruck } from '@react-icons/all-files/fi/FiTruck';

import { ContactMessageEntry } from '../../../../../../services/marketplace/generated/kubb/models/ContactMessageEntry';
import { GetLeadByExternalId200 } from '../../../../../../services/marketplace/generated/kubb/models/GetLeadByExternalId';
import { GetLeadByExternalIdPostAcceptanceChecklist200 } from '../../../../../../services/marketplace/generated/kubb/models/GetLeadByExternalIdPostAcceptanceChecklist';
import { LeadDetailStatusEnumKey } from '../../../../../../services/marketplace/generated/kubb/models/LeadDetail';
import { getAddressValue } from '../../../../../../utils/address';
import {
  formatDateISO,
  formatStageTimestamp,
  parseAndFormatDate,
} from '../../../../../../utils/dates';
import { pluralize, snakeCaseToSpaceSeparated, titleCase } from '../../../../../../utils/helpers';
import { INPUT_FILE_TYPES } from '../../../../../../utils/media';
import { WonConversation, getMessagesByOrg } from '../conversation/utils';

export type AcceptedOffer = {
  amount: number;
  orgName?: string | null;
  repName?: string | null;
  type: string;
  updatedAt?: string;
};

export const buildWonTarget = (
  acceptedOffer: AcceptedOffer | undefined,
  lead: GetLeadByExternalId200,
): WonConversation | null =>
  acceptedOffer?.orgName
    ? {
        kind: 'won',
        dealershipName: acceptedOffer.orgName,
        repName: acceptedOffer.repName,
        amount: acceptedOffer.amount,
        acceptedAt: lead.acceptedAt ?? acceptedOffer.updatedAt,
        history: (lead.offers ?? []).filter(
          (offer) =>
            offer.orgName === acceptedOffer.orgName &&
            offer.type !== 'driver_ask' &&
            offer.amount > 0,
        ),
      }
    : null;

export const getWonThread = (
  wonTarget: WonConversation | null,
  messages: ContactMessageEntry[] | undefined,
): ContactMessageEntry[] | undefined =>
  wonTarget != null ? getMessagesByOrg(messages).get(wonTarget.dealershipName) : undefined;

export type StageKey = 'accepted' | 'finish' | 'signing' | 'pickup' | 'finalized';

export type StageRenderState = 'done' | 'current-active' | 'current-waiting' | 'upcoming';

export const STAGE_ICONS: Record<StageKey, IconType> = {
  accepted: FiCheck,
  finish: FiCamera,
  signing: FiEdit3,
  pickup: FiTruck,
  finalized: FiFlag,
};

const currentNodeStyle = {
  bg: 'leaseEndBlue',
  color: 'white',
  borderColor: 'leaseEndBlue',
  boxShadow: '0 0 0 4px var(--chakra-colors-leaseEndBlueGlow)',
};

export const nodeStyleByState: Record<
  StageRenderState,
  { bg: string; color: string; borderColor: string; boxShadow?: string }
> = {
  done: { bg: 'leaseEndGreen', color: 'white', borderColor: 'leaseEndGreen' },
  'current-active': currentNodeStyle,
  'current-waiting': currentNodeStyle,
  upcoming: { bg: 'white', color: 'gainsboro', borderColor: 'platinum' },
};

type StageConfig = {
  key: StageKey;
  label: string;
};

export const STAGES: readonly StageConfig[] = [
  { key: 'accepted', label: 'Accepted' },
  { key: 'finish', label: 'Finish sale' },
  { key: 'signing', label: 'Signing' },
  { key: 'pickup', label: 'Pickup' },
  { key: 'finalized', label: 'Done' },
] as const;

export const STAGE_INDEX: Record<StageKey, number> = STAGES.reduce(
  (acc, stage, index) => ({ ...acc, [stage.key]: index }),
  {} as Record<StageKey, number>,
);

// Maps each active post-acceptance status to the stage it makes current, and
// whether the driver's part of that stage is done but waiting on the status to
// advance (the second of a two-status stage). `accepted` itself lands on Finish
// your sale because the Accepted stage is always shown done in the hub.
const STATUS_TO_STAGE: Partial<
  Record<LeadDetailStatusEnumKey, { stage: StageKey; waiting: boolean }>
> = {
  accepted: { stage: 'finish', waiting: false },
  confirmed: { stage: 'finish', waiting: true },
  contracts_sent: { stage: 'signing', waiting: false },
  awaiting_pickup: { stage: 'pickup', waiting: false },
  picked_up: { stage: 'pickup', waiting: true },
  billed: { stage: 'finalized', waiting: false },
  // Terminal: the whole spine is complete.
  sold: { stage: 'finalized', waiting: true },
};

export type StageView = {
  key: StageKey;
  label: string;
  state: StageRenderState;
  timestamp?: string;
};

export const stageTimestampsFromTimeline = (
  lead: Pick<GetLeadByExternalId200, 'acceptedAt' | 'stageTimeline'>,
): Partial<Record<StageKey, string | null>> => {
  const reachedAt = new Map(lead.stageTimeline.map((entry) => [entry.status, entry.timestamp]));
  return {
    accepted: lead.acceptedAt,
    finish: reachedAt.get('confirmed') ?? null,
    signing: reachedAt.get('awaiting_pickup') ?? null,
    pickup: reachedAt.get('picked_up') ?? null,
    finalized: reachedAt.get('sold') ?? null,
  };
};

export const resolveStages = (
  status: LeadDetailStatusEnumKey,
  stageTimestamps?: Partial<Record<StageKey, string | null>>,
): StageView[] => {
  const target = STATUS_TO_STAGE[status] ?? { stage: 'finish', waiting: false };
  const currentIndex = STAGE_INDEX[target.stage];

  return STAGES.map((config, index) => {
    let state: StageRenderState;
    if (index < currentIndex) {
      state = 'done';
    } else if (index > currentIndex) {
      state = 'upcoming';
    } else if (target.waiting) {
      // Finalized's "waiting" status (sold) is the terminal done state.
      state = config.key === 'finalized' ? 'done' : 'current-waiting';
    } else {
      state = 'current-active';
    }

    const timestamp = state === 'done' ? stageTimestamps?.[config.key] ?? undefined : undefined;

    return {
      key: config.key,
      label: config.label,
      state,
      timestamp,
    };
  });
};

export const currentStageKey = (status: LeadDetailStatusEnumKey): StageKey =>
  (STATUS_TO_STAGE[status] ?? { stage: 'finish' }).stage;

export const hasReachedSigning = (status: LeadDetailStatusEnumKey): boolean =>
  STAGE_INDEX[currentStageKey(status)] >= STAGE_INDEX.signing;

const SIGNING_WAIT_COPY = {
  headline: 'All done, nice work',
  subcopy:
    "Great job getting us everything. Keep an eye out for documents to sign, we'll let you know when they're ready.",
};

const FINALIZED_COPY = { headline: 'All done', subcopy: 'Your sale is complete.' };

export type PickupWindowKey = 'morning' | 'afternoon' | 'evening';

export const PICKUP_WINDOWS: readonly { key: PickupWindowKey; label: string; range: string }[] = [
  { key: 'morning', label: 'Morning', range: '8a to 12p' },
  { key: 'afternoon', label: 'Afternoon', range: '12p to 5p' },
  { key: 'evening', label: 'Evening', range: '5p to 8p' },
];

type PickupAnswer = NonNullable<GetLeadByExternalId200['pickupAvailability']>;

// Mirrors the marketplace endpoint's eligibility: the lead takes an answer
// from the moment every signature is in through awaiting_pickup, once ever.
export const pickupAvailabilityState = (
  lead: Pick<GetLeadByExternalId200, 'status' | 'signingStatus' | 'pickupAvailability'>,
): 'hidden' | 'ask' | 'answered' => {
  if (lead.pickupAvailability != null) return 'answered';
  if (lead.status === 'awaiting_pickup') return 'ask';
  if (lead.status === 'contracts_sent' && lead.signingStatus === 'completed') return 'ask';
  return 'hidden';
};

// Windows arrive in morning/afternoon/evening order from both the picker and the server.
export const formatPickupDaySummary = (date: string, windows: PickupWindowKey[]): string =>
  `${parseAndFormatDate(date, 'EEE MMM d')} ${windows.join(' or ')}`;

export const pickupAnswerSummary = (answer: PickupAnswer): string => {
  const days = answer.dates
    .map((entry) => formatPickupDaySummary(entry.date, entry.windows))
    .join(' · ');
  return answer.note ? `${days} · "${answer.note}"` : days;
};

const allDatesPassed = (answer: PickupAnswer): boolean => {
  const today = formatDateISO(new Date());
  return answer.dates.every((entry) => entry.date < today);
};

export const stageCardCopy = (
  lead: Pick<GetLeadByExternalId200, 'status' | 'signingStatus' | 'pickupAvailability'>,
  dealershipName: string,
): { headline: string; subcopy: string } => {
  if (currentStageKey(lead.status) === 'finalized') return FINALIZED_COPY;
  if (lead.status === 'picked_up') {
    return { headline: 'Picked up', subcopy: `${dealershipName} has the car.` };
  }
  const answer = lead.pickupAvailability;
  if (answer != null) {
    return {
      headline: 'Signed, moving to pickup',
      subcopy: allDatesPassed(answer)
        ? `Those dates have passed, ${dealershipName} will set a new time with you.`
        : `${dealershipName} has your times and will reach out to lock one in.`,
    };
  }
  if (pickupAvailabilityState(lead) === 'ask') {
    return { headline: 'Signed, nice work', subcopy: "Now let's get the pickup arranged." };
  }
  return SIGNING_WAIT_COPY;
};

const TIMELINE_SUBTITLES: Partial<Record<StageKey, string>> = {
  signing: 'Look out for documents to sign',
  pickup: 'Share your times, then the buyer picks up the car.',
  finalized: 'Payout confirmation lands here.',
};

export const subtitleFor = (
  stage: StageView,
  requiredRemaining: number,
  signingStatus?: GetLeadByExternalId200['signingStatus'],
): string => {
  if (stage.state === 'done') {
    const when = stage.timestamp ? formatStageTimestamp(stage.timestamp) : '';
    return stage.key === 'finish' ? `Done${when ? `, ${when}` : ''}` : when;
  }
  if (stage.key === 'finish') {
    return stage.state === 'current-waiting'
      ? 'Documents received, under review'
      : `In progress, ${requiredRemaining} ${pluralize('to-do', requiredRemaining)} left`;
  }
  if (stage.key === 'signing') {
    if (stage.state === 'upcoming') return 'Up next';
    // The signing packet is out and waiting on the driver's signature.
    if (
      stage.state === 'current-active' &&
      (signingStatus === 'pending' || signingStatus === 'partial')
    ) {
      return 'Paperwork ready, check your email or texts for the signing link';
    }
  }
  return TIMELINE_SUBTITLES[stage.key] ?? '';
};

type TodoBase = {
  label: string;
  help: string;
  icon: IconType;
};

// Which marketplace endpoint an upload goes to: lead documents or lead photos.
export type UploadKind = 'document' | 'photo';

// Upload todos open a file picker with `accept`; modal todos open their own modal.
type UploadTodoDefinition = TodoBase & {
  key: string;
  kind: UploadKind;
  accept: string;
};

export type ModalTodoDefinition = TodoBase & {
  key: 'confirm_address' | 'car_photos' | 'pickup_availability';
};

export type TodoDefinition = UploadTodoDefinition | ModalTodoDefinition;

export const isUploadTodo = (definition: TodoDefinition): definition is UploadTodoDefinition =>
  'accept' in definition;

export const ACCEPTED_FILE_TYPES = INPUT_FILE_TYPES.join(',');

// Images only, restricted to types the documents upload endpoint accepts.
const PHOTO_FILE_TYPES = INPUT_FILE_TYPES.filter((type) => type.startsWith('image/')).join(',');

export const acceptForKind = (kind: UploadKind): string =>
  kind === 'photo' ? PHOTO_FILE_TYPES : ACCEPTED_FILE_TYPES;

export const PICKUP_AVAILABILITY_TODO: ModalTodoDefinition = {
  key: 'pickup_availability',
  label: 'Share your pickup availability',
  help: 'Tell us when the buyer can come get the car.',
  icon: FiCalendar,
};

export const CONFIRM_ADDRESS_TODO: ModalTodoDefinition = {
  key: 'confirm_address',
  label: 'Confirm pickup address',
  help: 'Where the buyer picks up your car.',
  icon: FiMapPin,
};

export const CAR_PHOTOS_TODO: ModalTodoDefinition = {
  key: 'car_photos',
  label: 'Remaining photos of your car',
  help: 'Front, rear, sides, back seat, trunk.',
  icon: FiImage,
};

const UPLOAD_PRESENTATION: Record<string, TodoBase> = {
  'document:drivers_license': {
    label: "Driver's license",
    help: 'Front of your license.',
    icon: FiCreditCard,
  },
  'document:registration': {
    label: 'Vehicle registration',
    help: 'Your current registration card.',
    icon: FiFileText,
  },
  'photo:odometer': {
    label: 'Current odometer photo',
    help: 'Your most recent mileage, right off the dash.',
    icon: FiCamera,
  },
};

export const uploadPresentation = (kind: UploadKind, key: string, serverLabel?: string): TodoBase =>
  UPLOAD_PRESENTATION[`${kind}:${key}`] ?? {
    label: serverLabel ?? titleCase(snakeCaseToSpaceSeparated(key)),
    help: kind === 'photo' ? 'Upload this photo.' : 'Upload this document.',
    icon: kind === 'photo' ? FiCamera : FiFileText,
  };

export const uploadTodoDefinition = (
  kind: UploadKind,
  key: string,
  serverLabel?: string,
): UploadTodoDefinition => ({
  key,
  kind,
  accept: acceptForKind(kind),
  ...uploadPresentation(kind, key, serverLabel),
});

// Degraded todo set shown when the checklist fetch fails with nothing cached.
export const FALLBACK_UPLOAD_TODOS = Object.keys(UPLOAD_PRESENTATION).map((presentationKey) => {
  const [kind, key] = presentationKey.split(':');
  return uploadTodoDefinition(kind as UploadKind, key);
});

type ChecklistSection = GetLeadByExternalIdPostAcceptanceChecklist200['sections'][number];

export const findChecklistSection = <Kind extends ChecklistSection['kind']>(
  checklist: GetLeadByExternalIdPostAcceptanceChecklist200 | undefined,
  kind: Kind,
): Extract<ChecklistSection, { kind: Kind }> | undefined =>
  checklist?.sections.find(
    (section): section is Extract<ChecklistSection, { kind: Kind }> => section.kind === kind,
  );

export const formatPickupAddress = (
  lead: Pick<GetLeadByExternalId200, 'addressLine1' | 'addressLine2' | 'city' | 'state' | 'zip'>,
): string =>
  getAddressValue({
    address_line: lead.addressLine1,
    address_line_2: lead.addressLine2,
    city: lead.city,
    state: lead.state,
    zip: lead.zip,
  });
